/**
 * useInventoryRouterSearch Hook
 *
 * URL-driven state hook for the inventory list page.
 * Syncs React state with URL search params without triggering Turbo navigation.
 *
 * URL params are only written when the user interacts (not on initial load).
 * Sort/direction default to empty — the backend applies its own default sort.
 *
 * Follows the same pattern as useOrdersRouterSearch.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { InventoryStatus } from '../components/Inventory/types';

export interface InventorySearchParams {
  status: InventoryStatus;
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc' | '';
}

const defaultParams: InventorySearchParams = {
  status: 'active',
  q: '',
  page: 1,
  perPage: 50,
  sort: '',
  direction: '',
};

function parseSearchParams(urlSearchParams: URLSearchParams): InventorySearchParams {
  return {
    status: (urlSearchParams.get('status') as InventoryStatus) || defaultParams.status,
    q: urlSearchParams.get('q') || defaultParams.q,
    page: parseInt(urlSearchParams.get('page') || '1', 10) || defaultParams.page,
    perPage: parseInt(urlSearchParams.get('per_page') || String(defaultParams.perPage), 10) || defaultParams.perPage,
    sort: urlSearchParams.get('sort') || defaultParams.sort,
    direction: (urlSearchParams.get('direction') as 'asc' | 'desc') || defaultParams.direction,
  };
}

export function useInventoryRouterSearch(initialOverrides?: Partial<InventorySearchParams>) {
  const [params, setParams] = useState<InventorySearchParams>(() => {
    const urlParams = parseSearchParams(new URLSearchParams(window.location.search));
    return { ...urlParams, ...initialOverrides };
  });

  const syncToUrl = useCallback((newParams: InventorySearchParams) => {
    const url = new URL(window.location.href);

    // Status: only include if non-default
    if (newParams.status !== defaultParams.status) {
      url.searchParams.set('status', newParams.status);
    } else {
      url.searchParams.delete('status');
    }

    // Search query: only include if non-empty
    if (newParams.q) {
      url.searchParams.set('q', newParams.q);
    } else {
      url.searchParams.delete('q');
    }

    // Page: only include if non-default
    if (newParams.page !== defaultParams.page) {
      url.searchParams.set('page', String(newParams.page));
    } else {
      url.searchParams.delete('page');
    }

    // Per page: only include if non-default
    if (newParams.perPage !== defaultParams.perPage) {
      url.searchParams.set('per_page', String(newParams.perPage));
    } else {
      url.searchParams.delete('per_page');
    }

    // Sort: only include if non-empty
    if (newParams.sort) {
      url.searchParams.set('sort', newParams.sort);
      url.searchParams.set('direction', newParams.direction || 'asc');
    } else {
      url.searchParams.delete('sort');
      url.searchParams.delete('direction');
    }

    window.history.replaceState({}, '', url.toString());
  }, []);

  const navigate = useCallback(
    (updates: Partial<InventorySearchParams>) => {
      setParams(prev => {
        const newParams = { ...prev, ...updates };
        syncToUrl(newParams);
        return newParams;
      });
    },
    [syncToUrl]
  );

  useEffect(() => {
    const handlePopState = () => {
      const newParams = parseSearchParams(new URLSearchParams(window.location.search));
      setParams(newParams);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return useMemo(
    () => ({
      search: params,
      navigate,
      setStatus: (status: InventoryStatus) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: 'asc' | 'desc' | '') => navigate({ sort, direction, page: 1 }),
    }),
    [params, navigate]
  );
}
